import {
  CouponStatus,
  CurrentPayPlan,
  FlowType,
  MembershipLevel,
  PriceCart,
  VoucherStatus,
} from '@ace-clublabs/common-membership-api-types';
import {
  GenderCode,
  IAdditionalBenefits,
  IDisclaimers,
  IGreenBoxMessage,
  NewJoinConfirmationData,
  GiftDetails,
  Address,
  PartnerIntegration,
  IPartnerEnrollmentStatus,
  IPartnerIntegration,
  IBenefitsComparison,
} from '@lib/types';
import { memberTempCache } from '../../integrations/cache';
import { PartnerEnrollmentProcessStatus } from '../../utils/partnerEnrollmentHelper';

export type NewJoinSessionData = {
  sessionId: string | null | undefined;
  offerToken: string | null | undefined;
  campaignCode: string | null | undefined;
  clubCode: string | null | undefined;
  region: string | null | undefined;
  flowType: FlowType | null | undefined;
  statusDescription: string | null | undefined;
  zipCode: string | null | undefined;
  step: string;
  formData: NewMembershipFormData;
  confirmationData?: NewJoinConfirmationData;
  completedPartnerEnrollment: boolean;
  completedRegistration: boolean;
  priceCart?: PriceCart | null | undefined;
  membershipContent?: MembershipContent | null | undefined;
  queryString?: string | null | undefined;
  tokenStatus?: VoucherStatus | CouponStatus | null;
  partnerOffers?: {
    availableOffers: IPartnerIntegration[] | null | undefined;
    enrollmentEmail: string | null | undefined;
    enrollmentStatus: PartnerEnrollmentProcessStatus | null | undefined;
    partnerStatuses: IPartnerEnrollmentStatus[] | null | undefined;
  };
  enableGenerateLead?: boolean | null | undefined;
  enableDobField?: boolean | null | undefined;
};

export type NewMembershipFormData = {
  residenceAddress: Address;
  mailingAddress?: Address | undefined;
  mobileNumber: string | null;
  mobileConsent: boolean | null;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  email: string | null;
  gender: GenderCode | null;
  password: string | null;
  confirmPassword: string | null;
  associates?: AssociateInfo[];
  products?: ProductType[] | undefined;
  enrollments?: Enrollments[] | undefined;
  billPlan?: CurrentPayPlan | undefined;
  previousAnnualBillPlan?: CurrentPayPlan | undefined;
  firstSelectedMembershipLevel?: MembershipLevel | undefined;
  membershipLevel?: MembershipLevel | undefined;
  declineMagazine?: boolean | undefined;
  paperlessBilling?: boolean | undefined;
  declinePhysicalCard?: boolean | undefined;
  giftDetails?: GiftDetails | undefined;
  partnerEnrollments?: PartnerEnrollments | undefined;
  generateAutoLead?: boolean | undefined;
  hasPriorERSUsage?: boolean | undefined;
  hasPriorERSUsageKey?: string | undefined;
};

export type AssociateInfo = {
  virtualId: string | null;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  gender: GenderCode | null;
  roleType: RoleType;
};

export enum RoleType {
  PRIMARY = 'PR',
  ADULT_ASSOCIATE = 'AD',
  ASSOCIATE = 'AS',
  DEPENDENT = 'DE',
}

export enum ProductType {
  RV = 'RV',
  ERS = 'ERS',
}

export enum Enrollments {
  PAPERLESS = 'PAPERLESS',
  DIGITAL_CARD = 'DIGITAL_CARD',
}

export type MembershipContent = {
  greenBoxMessage?: IGreenBoxMessage | null;
  additionalBenefits: IAdditionalBenefits[] | null;
  disclaimerMessage: IDisclaimers | null;
  benefitsComparison: IBenefitsComparison[] | null;
};

export type PartnerEnrollments = {
  [key in PartnerIntegration]: boolean;
};

/***
 * Function defined at the Web Client level because it deals strictly with the collection of data from the form.  These
 * data structures and the associated functions should never be used outside of the web client.  Returns an unpopulated
 * state structure for a form to be saved in the browser session.
 * @param zipCode Allowed to be null, but should always be collected at outset of execution
 */
export const getDefaultFormData: (
  zipCode?: string | null,
  offerToken?: string | null,
  campaignCode?: string | null,
  flowType?: FlowType,
  statusDescription?: string | null,
  PriceCart?: PriceCart | null
) => NewJoinSessionData = (zipCode, offerToken, campaignCode, flowType, statusDescription, priceCart) => {
  const existingForm = memberTempCache.get();

  const outSessionData = {
    sessionId: null,
    offerToken: offerToken ?? null,
    campaignCode: campaignCode ?? null,
    clubCode: null,
    region: null,
    statusDescription: statusDescription ?? null,
    zipCode: zipCode ?? null,
    flowType,
    step: 'landing',
    completedRegistration: false,
    completedPartnerEnrollment: false,
    priceCart: priceCart ?? null,
    formData: {
      firstName: null,
      lastName: null,
      dateOfBirth: null,
      email: null,
      gender: null,
      mobileNumber: null,
      mobileConsent: null,
      residenceAddress: {
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
      },
      password: null,
      confirmPassword: null,
      billPlan: undefined,
      previousBillPlan: undefined,
      declineMagazine: undefined,
      paperlessBilling: undefined,
      declinePhysicalCard: undefined,
      ...existingForm,
    },
  };

  memberTempCache.delete();

  return outSessionData;
};
